import React, { useContext, useEffect, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import pMinDelay from 'p-min-delay';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { ThemeContext } from '../../../context/ThemeContext';
import { getExpenseTagStats } from '../../../services/TransactionService';
import { colorsForLabels } from '../../../utils/insightPalette';
import { formatAmount } from '../../../utils/helpers';

const AssetsChart = loadable(() =>
  pMinDelay(import('./Dashboard/AssetsChart'), 1000)
);

const PERIOD_OPTIONS = [
  { label: 'All time', value: 'all' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: '90d', value: '90d' },
];

const getStartDateForPeriod = (period) => {
  if (period === 'all') return null;
  const days = { '7d': 7, '30d': 30, '90d': 90 };
  const date = new Date();
  date.setDate(date.getDate() - days[period]);
  return date.toISOString().split('T')[0];
};

const SkeletonRow = () => (
  <div className="d-flex align-items-center gap-3 py-2">
    <span className="rounded-circle bg-light" style={{ width: 12, height: 12 }} />
    <div className="flex-grow-1">
      <div className="bg-light rounded" style={{ height: 10, width: '60%' }} />
      <div className="bg-light rounded mt-2" style={{ height: 6, width: '100%' }} />
    </div>
    <div className="bg-light rounded" style={{ height: 12, width: 70 }} />
  </div>
);

const SpendingByExpenseTagCard = () => {
  const partnerId = useSelector((state) => state.auth.authUser?.partner_id);

  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const startDate = getStartDateForPeriod(selectedPeriod);

  const {
    data: expenseTagsStats,
    isFetching: expenseTagsStatsLoading,
    refetch,
  } = useQuery({
    queryKey: ['partnerExpenseTagsStats', partnerId, selectedPeriod],
    queryFn: () => getExpenseTagStats(partnerId, startDate).then((res) => res.data.data),
    enabled: !!partnerId,
    staleTime: 0,
  });

  const rankedTags = useMemo(() => {
    if (!expenseTagsStats) return [];
    const { expenseNames = [], totalAmountsSpent = [] } = expenseTagsStats;
    const totalSpent = totalAmountsSpent.reduce((sum, amount) => sum + amount, 0);
    const rows = expenseNames.map((name, index) => {
      const amount = totalAmountsSpent[index] ?? 0;
      const percentage = totalSpent > 0 ? (amount / totalSpent) * 100 : 0;
      return { name, amount, percentage };
    });
    rows.sort((a, b) => b.amount - a.amount);
    return rows;
  }, [expenseTagsStats]);

  const chartColors = useMemo(
    () => colorsForLabels(expenseTagsStats?.expenseNames ?? []),
    [expenseTagsStats?.expenseNames],
  );

  const rankedColors = useMemo(
    () => colorsForLabels(rankedTags.map((row) => row.name)),
    [rankedTags],
  );

  const totalSpent = useMemo(
    () => rankedTags.reduce((sum, row) => sum + row.amount, 0),
    [rankedTags],
  );

  const hasData = rankedTags.length > 0 && totalSpent > 0;

  return (
    <div className="card">
      <div className="card-header border-0 pb-0 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="card-title mb-2">Spending by Expense Tag</h4>
          <div className="d-flex flex-wrap gap-2">
            {PERIOD_OPTIONS.map(({ label, value }) => (
              <button
                key={value}
                type="button"
                className={`btn btn-sm ${selectedPeriod === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setSelectedPeriod(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          className="d-flex align-items-center justify-content-center"
          onClick={refetch}
          title="Refresh"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            border: '2px solid #198754',
            color: '#198754',
            background: 'white',
            padding: 0,
          }}
        >
          <i className={`bi bi-arrow-clockwise ${expenseTagsStatsLoading ? 'spin' : ''}`}></i>
        </button>
      </div>

      <div className="card-body pt-3">
        {expenseTagsStatsLoading && !expenseTagsStats ? (
          <div className="row align-items-center g-4">
            <div className="col-12 col-md-5 text-center">
              <div
                className="rounded-circle bg-light mx-auto"
                style={{ width: 220, height: 220 }}
              />
            </div>
            <div className="col-12 col-md-7">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          </div>
        ) : !hasData ? (
          <p className="text-center text-muted py-4 mb-0">
            No spending in this period yet.
          </p>
        ) : (
          <>
            <div className="row g-3 mb-4">
              <div className="col-6 col-sm-4">
                <div className="text-muted small">Total spent</div>
                <div className="fs-3 fw-semibold">KES {formatAmount(totalSpent)}</div>
              </div>
              <div className="col-6 col-sm-4">
                <div className="text-muted small">Categories</div>
                <div className="fs-3 fw-semibold">{rankedTags.length}</div>
              </div>
            </div>

            <div className="row align-items-center g-4">
              <div className="col-12 col-md-5 text-center">
                <div className="custome-donut">
                  <AssetsChart
                    expenseTagsStats={expenseTagsStats}
                    randomColors={chartColors}
                  />
                </div>
              </div>

              <div className="col-12 col-md-7">
                <div className="d-flex flex-column gap-3">
                  {rankedTags.map((row, index) => (
                    <div key={row.name}>
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <span className="d-inline-flex align-items-center gap-2 fs-14 fw-semibold text-body">
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{
                              width: 10,
                              height: 10,
                              backgroundColor: rankedColors[index],
                            }}
                          />
                          {row.name}
                        </span>
                        <span className="fs-14 fw-semibold text-body">
                          KES {formatAmount(row.amount)}
                        </span>
                      </div>
                      <div
                        className="progress mt-2"
                        style={{ height: 4, backgroundColor: '#f0f0f5' }}
                      >
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{
                            width: `${row.percentage}%`,
                            backgroundColor: rankedColors[index],
                          }}
                          aria-valuenow={row.percentage}
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-label={`${row.name} share`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PartnerExpenseTagStats = () => {
  const { changeBackground } = useContext(ThemeContext);
  useEffect(() => {
    changeBackground({ value: 'light', label: 'Light' });
  }, []);

  return (
    <div className="row g-3">
      <div className="col-12">
        <SpendingByExpenseTagCard />
      </div>
    </div>
  );
};

export default PartnerExpenseTagStats;
